// Same-origin API client. The access token is held in MEMORY ONLY — never
// localStorage, so a script injection can't lift a persisted token. A reload
// stays signed in via the httpOnly refresh cookie the browser sends
// automatically to /auth. On a 401 we transparently try one refresh and replay
// the request.

import type { TokenResponse } from "./types";

let accessToken: string | null = null;
let onAuthLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Register a callback fired when refresh fails (session truly gone). */
export function setOnAuthLost(cb: () => void): void {
  onAuthLost = cb;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

/** The user-facing message for a caught error: ApiError's own, else fallback. */
export function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

interface RequestOptions {
  json?: unknown;
  form?: FormData;
  auth?: boolean; // attach the bearer token (default true)
  headers?: Record<string, string>; // extra headers (e.g. Idempotency-Key)
}

// When the server gives no usable `detail`, tell the user what to DO, not
// just that something failed. 429/5xx include retry guidance.
export function statusFallback(status: number): string {
  if (status === 429) return "Too many requests. Wait a minute, then try again.";
  if (status === 503 || status === 502 || status === 504)
    return "The service is briefly unavailable. Try again shortly.";
  if (status >= 500) return "Something went wrong on our end. Try again shortly.";
  if (status === 403) return "You don't have access to that.";
  if (status === 404) return "That wasn't found. It may have been removed.";
  return `Request failed (${status}).`;
}

// Requests that hang (bad network, dead proxy) abort after this long so the UI
// can show guidance instead of spinning forever. Uploads are exempt — photo
// batches on slow links legitimately exceed it.
const REQUEST_TIMEOUT_MS = 30_000;

function extractDetail(body: unknown, fallback: string): string {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    // FastAPI validation errors: detail is a list of {msg, loc}.
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  }
  return fallback;
}

async function raw(method: string, path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = { ...opts.headers };
  const auth = opts.auth ?? true;
  if (auth && accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let body: BodyInit | undefined;
  if (opts.form) {
    body = opts.form; // browser sets multipart boundary
  } else if (opts.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.json);
  }

  // No timeout on uploads (multipart form) — see REQUEST_TIMEOUT_MS.
  const signal = opts.form ? undefined : AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  try {
    return await fetch(path, { method, headers, body, credentials: "same-origin", signal });
  } catch (err) {
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new ApiError(0, "The request timed out. Check your connection and try again.");
    }
    // Keep the underlying browser error visible — "Failed to fetch" hides
    // real causes (e.g. a file that changed on disk after being selected).
    const cause = err instanceof Error && err.message ? ` (${err.message})` : "";
    throw new ApiError(0, `Can't reach the server. Check your connection and try again.${cause}`);
  }
}

let refreshing: Promise<boolean> | null = null;
// Whether the last failed refresh was "no cookie at all" (an anonymous
// visitor) vs a revoked/rotated token — bootstrapSession retries only the
// latter, which can be a concurrent tab winning the single-use rotation.
let refreshHadNoCookie = false;

/** Try to mint a new access token from the refresh cookie. De-duped. */
async function tryRefresh(): Promise<boolean> {
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await raw("POST", "/auth/refresh", { auth: false });
        if (!res.ok) {
          refreshHadNoCookie = false;
          try {
            const body = (await res.json()) as { detail?: unknown };
            // Matches the API's 401 detail for an absent cookie (auth/router.py).
            refreshHadNoCookie =
              typeof body.detail === "string" && body.detail.includes("Missing refresh token");
          } catch {
            // non-JSON error body — treat as a revoked-token failure
          }
          return false;
        }
        const data = (await res.json()) as TokenResponse;
        setAccessToken(data.access_token);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
  }
  return refreshing;
}

/** Boot-time session restore: redeem the httpOnly refresh cookie directly
 * (the access token is memory-only, so booting via a guaranteed-401 API call
 * would waste a round trip). When the refresh fails on a PRESENT cookie, a
 * concurrent tab may have just won the single-use rotation and written a
 * fresh cookie — retry once before rendering signed out. */
export async function bootstrapSession(): Promise<boolean> {
  if (await tryRefresh()) return true;
  if (refreshHadNoCookie) return false; // anonymous visitor — no retry
  await new Promise((resolve) => setTimeout(resolve, 400));
  return tryRefresh();
}

/** A unique Idempotency-Key. crypto.randomUUID exists only in secure contexts
 * (HTTPS or localhost) — the plain-HTTP staging/dev tiers need the fallback. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const rand = () => Math.random().toString(36).slice(2);
  return `${Date.now().toString(36)}-${rand()}-${rand()}`;
}

/** GET a paginated list endpoint page-by-page until exhausted, returning the
 * full list — the server caps pages at 500, and the SPA views (photo gallery,
 * collab tables) render complete lists rather than paging UIs. */
export async function apiList<T>(path: string, pageSize = 500): Promise<T[]> {
  const sep = path.includes("?") ? "&" : "?";
  const out: T[] = [];
  for (let offset = 0; ; offset += pageSize) {
    const page = await api<T[]>("GET", `${path}${sep}limit=${pageSize}&offset=${offset}`);
    out.push(...page);
    if (page.length < pageSize) return out;
  }
}

export async function api<T>(method: string, path: string, opts: RequestOptions = {}): Promise<T> {
  let res = await raw(method, path, opts);

  if (res.status === 401 && (opts.auth ?? true)) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await raw(method, path, opts);
    } else {
      setAccessToken(null);
      onAuthLost?.();
    }
  }

  if (res.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    throw new ApiError(res.status, extractDetail(payload, statusFallback(res.status)));
  }
  return payload as T;
}

/** Like api(), but returns the raw body as a Blob — for binary downloads (ZIPs)
 * that text/JSON parsing would corrupt. Same transparent 401 → refresh → replay. */
export async function apiBlob(
  method: string,
  path: string,
  opts: RequestOptions = {},
): Promise<Blob> {
  let res = await raw(method, path, opts);

  if (res.status === 401 && (opts.auth ?? true)) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await raw(method, path, opts);
    } else {
      setAccessToken(null);
      onAuthLost?.();
    }
  }

  if (!res.ok) {
    let payload: unknown = null;
    try {
      payload = JSON.parse(await res.text());
    } catch {
      // non-JSON error body; fall through to the generic message
    }
    throw new ApiError(res.status, extractDetail(payload, statusFallback(res.status)));
  }
  return res.blob();
}
